<template>
  <v-card
    flat
    class="pa-3 mt-2"
  >
    <v-card-text>
      <v-alert
        v-if="!smsViaService"
        type="info"
        text
        dense
      >
        Сейчас API отправляет SMS напрямую провайдеру, мимо сервиса уведомлений. Настройки и
        шаблоны начнут действовать, когда в окружении API включат отправку через сервис.
      </v-alert>

      <channel-state :channel-state="smsState"></channel-state>

      <v-form
        ref="form"
        class="multi-col-validation mt-6"
      >
        <v-row>
          <v-col cols="12">
            <v-switch
              v-model="sms.isEnabled"
              label="Канал включён"
              hide-details
              class="mt-0"
            ></v-switch>
          </v-col>

          <v-col
            cols="12"
            md="6"
          >
            <v-text-field
              v-model="sms.sender"
              label="Имя отправителя"
              :placeholder="smsState.defaultSender"
              :persistent-placeholder="!!smsState.defaultSender"
              :hint="senderHint"
              persistent-hint
              dense
              outlined
              :error-messages="messages('sender')"
            ></v-text-field>
          </v-col>

          <v-col
            cols="12"
            md="6"
          >
            <v-combobox
              v-model="sms.sandboxAllowedPhones"
              label="Белый список номеров стенда"
              hint="На стенде настоящие SMS уходят только этим номерам, остальным — запись в журнале"
              persistent-hint
              multiple
              chips
              small-chips
              deletable-chips
              append-icon=""
              dense
              outlined
              :delimiters="[',', ';']"
              :error-messages="messages('sandboxAllowedPhones')"
            ></v-combobox>
          </v-col>

          <v-col
            cols="12"
            md="4"
          >
            <v-text-field
              v-model="sms.maxPerPhonePerDay"
              label="Потолок SMS на один номер в сутки"
              hint="0 — без потолка"
              persistent-hint
              type="number"
              min="0"
              dense
              outlined
              :rules="[rules.required, rules.integerValidator]"
              :error-messages="messages('maxPerPhonePerDay')"
            ></v-text-field>
          </v-col>

          <v-col
            cols="12"
            md="4"
          >
            <v-text-field
              v-model="sms.maxPerHour"
              label="Потолок SMS в час на все номера"
              hint="0 — без потолка"
              persistent-hint
              type="number"
              min="0"
              dense
              outlined
              :rules="[rules.required, rules.integerValidator]"
              :error-messages="messages('maxPerHour')"
            ></v-text-field>
          </v-col>

          <v-col
            cols="12"
            md="4"
          >
            <v-text-field
              v-model="sms.journalRetentionDays"
              label="Срок хранения журнала SMS, дней"
              hint="0 — не удалять"
              persistent-hint
              type="number"
              min="0"
              dense
              outlined
              :rules="[rules.required, rules.integerValidator]"
              :error-messages="messages('journalRetentionDays')"
            ></v-text-field>
          </v-col>

          <v-col cols="12">
            <v-btn
              color="primary"
              class="me-3 mt-4"
              :loading="saving === 'sms'"
              :disabled="hasNotPermission('Permissions.NotificationSettingsUpdate')"
              @click="saveData"
            >
              Сохранить
            </v-btn>
          </v-col>
        </v-row>
      </v-form>

      <v-divider class="my-6"></v-divider>

      <span class="text-subtitle-2">Проверка</span>
      <p class="text-caption text--secondary">
        Проверочное SMS уходит через сервис уведомлений. Свой текст задать нельзя: он берётся
        из шаблона на вкладке «Шаблоны».
      </p>
      <v-row>
        <v-col
          cols="12"
          md="6"
        >
          <v-text-field
            v-model="testPhoneNumber"
            label="Номер телефона"
            dense
            outlined
            hide-details
          ></v-text-field>
        </v-col>

        <v-col
          cols="12"
          md="6"
        >
          <v-btn
            color="primary"
            outlined
            :loading="testSending"
            :disabled="!testPhoneNumber || hasNotPermission('Permissions.NotificationTestSend')"
            @click="sendTest"
          >
            Отправить проверочное SMS
          </v-btn>
        </v-col>

        <v-col
          v-if="testResult"
          cols="12"
        >
          <v-alert
            :type="testResult.type"
            text
            dense
            class="mb-0"
          >
            {{ testResult.text }}
          </v-alert>
        </v-col>
      </v-row>
    </v-card-text>
  </v-card>
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex'
import { mapFormHandlers } from '@/helpers/forms'
import { rules } from '@/helpers/validation'
import { errorMessages, hasPermission } from '@/helpers'
import ChannelState from '@/views/notification-settings/channel-state.vue'

export default {
  name: 'SmsSettingForm',
  components: { ChannelState },
  data: () => ({
    rules,
    testPhoneNumber: '',
    testSending: false,

    testResult: null,
  }),
  computed: {
    ...mapFormHandlers('notificationSettings', [
      'sms.isEnabled',
      'sms.sender',
      'sms.sandboxAllowedPhones',
      'sms.maxPerPhonePerDay',
      'sms.maxPerHour',
      'sms.journalRetentionDays',
    ]),
    ...mapState('notificationSettings', ['smsState', 'smsViaService', 'saving']),
    ...mapGetters('notificationSettings', ['errorsOf']),

    senderHint() {
      return this.smsState.defaultSender
        ? `Пусто — берётся имя из окружения сервиса: ${this.smsState.defaultSender}`
        : 'Пусто — берётся имя из окружения сервиса'
    },
  },
  methods: {
    ...mapActions('notificationSettings', ['update', 'sendTestSms']),
    hasNotPermission(value) {
      return !hasPermission(value)
    },
    messages(property) {
      return errorMessages(this.errorsOf('sms'), property)
    },
    async saveData() {
      if (this.$refs.form.validate()) {
        await this.update('sms')
      }
    },
    async sendTest() {
      this.testSending = true
      this.testResult = null

      try {
        await this.sendTestSms(this.testPhoneNumber)
        this.testResult = { type: 'success', text: 'Проверочное SMS отправлено' }
      } catch (error) {
        this.testResult = {
          type: 'error',
          text: error.response?.data?.message || 'Не удалось отправить проверочное SMS',
        }
      } finally {
        this.testSending = false
      }
    },
  },
}
</script>
